import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CatalogEntry } from "../data/catalogTypes";
import { ImageViewer } from "./ImageViewer";
import { routeHref } from "../routes";
import { ProgramArticle } from "./ProgramArticle";
import "./ProgramCard.scss";

type ProgramCardProps = {
  program: CatalogEntry;
  compact?: boolean;
};

type OpenView = "details" | "image" | null;

export function ProgramCard({ program, compact = false }: ProgramCardProps) {
  const [openView, setOpenView] = useState<OpenView>(null);
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const imageTriggerRef = useRef<HTMLButtonElement>(null);
  const bodyTriggerRef = useRef<HTMLDivElement>(null);
  const hasText = Boolean(program.eyebrow || program.title || program.description || program.mobileDescription || program.featuredDescription || program.featuredMobileDescription || program.status || program.details || program.article?.length);
  const contactHref = program.slug ? `${routeHref("/contact")}?program=${encodeURIComponent(program.slug)}` : routeHref("/contact");
  const title = program.title || "Programul asociației";
  const showImage = (src: string) => {
    setZoomImage(src);
    setOpenView("image");
  };
  const openCard = () => {
    if (hasText) setOpenView("details");
    else if (program.image) showImage(program.image);
  };

  useEffect(() => {
    if (openView !== "details") return;
    const previousOverflow = document.body.style.overflow;
    const imageTrigger = imageTriggerRef.current;
    const bodyTrigger = bodyTriggerRef.current;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenView(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      (imageTrigger || bodyTrigger)?.focus();
    };
  }, [openView]);

  return (
    <>
      <article
        className={"program-card" + (compact ? " program-card--compact" : "") + (!hasText ? " program-card--image-only" : "") + (!program.image ? " program-card--text-only" : "")}
        id={program.slug ? "program-" + program.slug : undefined}
      >
        {program.image && <button ref={imageTriggerRef} type="button" className="program-card__image" onClick={openCard} aria-label={hasText ? "Vezi detalii despre " + title : "Mărește imaginea"}>
          <img src={program.previewImage || program.image} alt={program.imageAlt || program.title || "Imagine program"} loading="lazy" style={{ objectPosition: program.imagePosition || "center" }} />
        </button>}
        {hasText && <div ref={bodyTriggerRef} role="button" tabIndex={0} className="program-card__body" onClick={openCard} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openCard(); } }} aria-label={"Vezi detalii despre " + title}>
          {program.eyebrow && <span className="eyebrow">{program.eyebrow}</span>}
          {program.title && <h3>{program.title}</h3>}
          {(program.description || program.featuredDescription) && <span className="program-card__description desktop-only">{compact ? (program.featuredDescription || program.description) : (program.description || program.featuredDescription)}</span>}
          {(program.description || program.mobileDescription || program.featuredMobileDescription) && <span className="program-card__description mobile-only">{compact ? (program.featuredMobileDescription || program.mobileDescription || program.description) : (program.mobileDescription || program.description || program.featuredMobileDescription)}</span>}
          <span className="program-card__footer">
            {program.status && <span>{program.status}</span>}
            <strong>Vezi detalii ↗</strong>
          </span>
        </div>}
      </article>

      {openView === "details" && createPortal(
        <div className="program-dialog-backdrop" onClick={event => { if (event.target === event.currentTarget) setOpenView(null); }}>
          <section className="program-dialog" role="dialog" aria-modal="true" aria-label={"Detalii despre " + title}>
            <button ref={closeButtonRef} type="button" className="program-dialog__close" aria-label="Închide detaliile" onClick={() => setOpenView(null)}>×</button>
            {program.image && <button type="button" className="program-dialog__image" onClick={() => program.image && showImage(program.image)} aria-label={"Mărește imaginea pentru " + title}>
              <img src={program.image} alt={program.imageAlt || program.title || "Imagine program"} />
              <span>Mărește imaginea ↗</span>
            </button>}
            <div className="program-dialog__content">
              {program.eyebrow && <p className="eyebrow">{program.eyebrow}</p>}
              <h2>{title}</h2>
              {program.status && <p className="program-dialog__status">{program.status}</p>}
              {program.article?.length ? <ProgramArticle blocks={program.article} onImageClick={showImage} /> : program.details ? <div className="program-dialog__text">{program.details}</div> : program.description && <p className="program-dialog__text">{program.description}</p>}
              <div className="program-dialog__links">
                <a href={contactHref} onClick={() => setOpenView(null)}>Cere detalii ↗</a>
              </div>
            </div>
          </section>
        </div>,
        document.body
      )}

      {openView === "image" && zoomImage && (
        <ImageViewer src={zoomImage} alt={program.imageAlt || program.title || "Imagine program"} onClose={() => setOpenView(null)} />
      )}
    </>
  );
}
